import React from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { AlertCircleIcon } from "lucide-react";

import { MultiPhotoUpload } from "@/components/fields/multi-photo-upload";
import { MultiVideoUploader } from "@/components/fields/multi-video-upload";
import { PhotoType } from "@/components/forms/schemas/media";
import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { VideoWithThumbnail } from "@/components/media/video-with-thumbnail";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { SortableList } from "@/components/ui/auto-form/fields/sortable";
import { Separator } from "@/components/ui/separator";

export type IMediaFieldCustomData = {
  collection: string;
};

export const MediaField: React.FC<AutoFormFieldProps> = ({
  id,
  field,
  inputProps,
}) => {
  const form = useFormContext();
  const { field: controlledField } = useController({ name: id });
  const media = useWatch({ name: id });

  const { key, ...props } = inputProps;

  if (!field.fieldConfig?.customData) {
    return (
      <Alert variant="destructive" className="max-w-md">
        <AlertCircleIcon />
        <AlertTitle>Oops</AlertTitle>
        <AlertDescription>
          Add collection prop in <code>customData</code> in order to parse
          <code>MediaField</code>
        </AlertDescription>
      </Alert>
    );
  }
  const customData = field.fieldConfig?.customData as IMediaFieldCustomData;

  const onPhotosChanged = (photos: PhotoType[], type: "replace" | "append") => {
    controlledField.onChange({
      ...media,
      photos: [...(type === "replace" ? [] : media?.photos || []), ...photos],
    });
  };

  const onTeasersChanged = (teasers: any[], type: "replace" | "append") => {
    controlledField.onChange({
      ...media,
      teasers: [
        ...(type === "replace" ? [] : media?.teasers || []),
        ...teasers,
      ],
    });
  };

  return (
    <div className={"flex flex-col space-y-2 text-foreground"}>
      <Separator />
      <div className="text-sm font-medium">Photos</div>
      <>
        <SortableList
          parentClassName="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2"
          items={
            media?.photos.map((p: any) => ({
              id: p.blurhash,
              ...p,
            })) || []
          }
          render={(p) => (
            <PhotoWithBlurhash key={p.url} url={p.url} blurhash={p.blurhash} />
          )}
          onChanged={(items) => {
            onPhotosChanged(items, "replace");
          }}
          canDelete={true}
        />
        <MultiPhotoUpload
          getPath={(_) =>
            `${customData.collection}/${form.getValues(
              "id",
            )}/${crypto.randomUUID()}.webp`
          }
          onUploadsComplete={(uploads) => {
            const newPhotos = uploads.map((u) => ({
              url: u.url,
              blurhash: u.blurhash,
              metadata: u.metadata,
            }));

            onPhotosChanged(newPhotos, "append");

            return false;
          }}
        />
      </>
      <div className="text-sm font-medium">Teasers</div>
      <>
        <SortableList
          parentClassName="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4"
          items={
            media?.teasers.map((t: any) => ({
              id: t.url,
              ...t,
            })) || []
          }
          render={(t) => (
            <VideoWithThumbnail
              key={t.url}
              url={t.previewUrl}
              blurhash={t.thumbnailBlurhash}
            />
          )}
          onChanged={(items) => {
            onTeasersChanged(items, "replace");
          }}
          canDelete={true}
        />
        <MultiVideoUploader
          getPath={(_) =>
            `${customData.collection}/${form.getValues(
              "id",
            )}/${crypto.randomUUID()}.webp`
          }
          onUploadsComplete={(uploads) => {
            const newTeasers = uploads.map((u) => ({
              url: u.manifestUrl,
              previewUrl: u.previewUrl,
              thumbnailUrl: u.thumbnailUrl,
              thumbnailBlurhash: u.thumbnailBlurhash,
            }));
            onTeasersChanged(newTeasers, "append");
            return false;
          }}
        />
      </>
    </div>
  );
};
